import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { shopSettingsApi, uploadApi } from '../api';
import LogoUploadCard from '../components/settings/LogoUploadCard';
import ThemeColorCard from '../components/settings/ThemeColorCard';
import ShopNameCard from '../components/settings/ShopNameCard';
import ShopTagCard from '../components/settings/ShopTagCard';
import DeliverySettingsCard from '../components/settings/DeliverySettingsCard';
import PaymentSettingsCard from '../components/settings/PaymentSettingsCard';
import KhrRateCard from '../components/settings/KhrRateCard';
import ContactInfoCard from '../components/settings/ContactInfoCard';
import BannerManageSection from '../components/settings/BannerManageSection';
import BannerFormDialog from '../components/settings/BannerFormDialog';

const ShopSettingsPage = () => {
  const { setShopSettings } = useApp();

  const [settings, setSettings] = useState({
    shop_name: '', shop_name_en: '', shop_name_km: '',
    shop_tag: '', shop_tag_en: '', shop_tag_km: '',
    logo_url: '', primary_color: '#F97316',
    phone: '', email: '', address: '', facebook: '', telegram: '',
    enable_pickup: 1, enable_delivery: 1,
    enable_khqr: 1, enable_alipay: 1, enable_usdt: 1, enable_cod: 1,
    khr_rate: 4200
  });
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showBannerForm, setShowBannerForm] = useState(false);
  const [bannerForm, setBannerForm] = useState({ image_url: '', title: '', link_url: '', sort_order: 0 });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await shopSettingsApi.getSettings();
      if (res.success && res.data) {
        const s = res.data;
        setSettings({
          shop_name:       s.shop_name      || '',
          shop_name_en:    s.shop_name_en   || '',
          shop_name_km:    s.shop_name_km   || '',
          shop_tag:        s.shop_tag       || '',
          shop_tag_en:     s.shop_tag_en    || '',
          shop_tag_km:     s.shop_tag_km    || '',
          logo_url:        s.logo_url       || '',
          primary_color:   s.primary_color  || '#F97316',
          phone:           s.phone          || '',
          email:           s.email          || '',
          address:         s.address        || '',
          facebook:        s.facebook       || '',
          telegram:        s.telegram       || '',
          enable_pickup:   s.enable_pickup   !== undefined ? s.enable_pickup   : 1,
          enable_delivery: s.enable_delivery !== undefined ? s.enable_delivery : 1,
          enable_khqr:     s.enable_khqr     !== undefined ? s.enable_khqr     : 1,
          enable_alipay:   s.enable_alipay   !== undefined ? s.enable_alipay   : 1,
          enable_usdt:     s.enable_usdt     !== undefined ? s.enable_usdt     : 1,
          enable_cod:      s.enable_cod      !== undefined ? s.enable_cod      : 1,
          khr_rate:        s.khr_rate       || 4200,
        });
        if (s.logo_url) setLogoPreview(s.logo_url);
        setBanners(s.banners || []);
      }
    } catch (err) {
      setError('加载设置失败');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleField = (field) => {
    setSettings(prev => ({ ...prev, [field]: prev[field] ? 0 : 1 }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('图片不能超过 5MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return settings.logo_url;
    setUploadingLogo(true);
    try {
      const res = await uploadApi.uploadProductImage(logoFile);
      if (res.success && res.data?.url) return res.data.url;
      throw new Error(res.message || '上传失败');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!settings.shop_name) { setError('店铺名称不能为空'); return; }
    if (!settings.khr_rate || settings.khr_rate < 100) { setError('KHR 汇率不能小于 100'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let logoUrl = settings.logo_url;
      if (logoFile) logoUrl = await uploadLogo();

      const res = await shopSettingsApi.updateSettings({ ...settings, logo_url: logoUrl });
      if (res.success) {
        setSuccess('保存成功！');
        setLogoFile(null);
        setSettings(prev => ({ ...prev, logo_url: logoUrl }));
        if (setShopSettings) setShopSettings(res.data);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleAddBanner = async () => {
    if (!bannerFile && !bannerForm.image_url) { alert('请上传广告图片'); return; }
    setSavingBanner(true);
    try {
      let imageUrl = bannerForm.image_url;
      if (bannerFile) {
        setUploadingBanner(true);
        const res = await uploadApi.uploadProductImage(bannerFile);
        setUploadingBanner(false);
        if (res.success && res.data?.url) imageUrl = res.data.url;
        else throw new Error(res.message || '上传失败');
      }
      const res = await shopSettingsApi.createBanner({ ...bannerForm, image_url: imageUrl });
      if (res.success) {
        setBanners(prev => [...prev, res.data]);
        setShowBannerForm(false);
        setBannerForm({ image_url: '', title: '', link_url: '', sort_order: 0 });
        setBannerFile(null);
        setBannerPreview('');
      } else {
        alert(res.message || '添加失败');
      }
    } catch (err) {
      alert(err.message || '添加失败');
    } finally {
      setSavingBanner(false);
      setUploadingBanner(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('确定删除这张广告图吗？')) return;
    try {
      const res = await shopSettingsApi.deleteBanner(id);
      if (res.success) setBanners(prev => prev.filter(b => b.id !== id));
      else alert(res.message || '删除失败');
    } catch (err) { alert(err.message || '删除失败'); }
  };

  const toggleBannerStatus = async (banner) => {
    const newStatus = banner.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await shopSettingsApi.updateBanner(banner.id, { status: newStatus });
      if (res.success) {
        setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, status: newStatus } : b));
      }
    } catch (err) { alert('操作失败'); }
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-screen">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">商家设置</h2>
          <p className="text-gray-600 mt-1">管理店铺基本信息、Logo 和广告图</p>
        </div>
        <button onClick={handleSave} disabled={saving || uploadingLogo}
          className="flex items-center space-x-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <Save className="w-4 h-4" />
          <span>{saving ? '保存中...' : '保存设置'}</span>
        </button>
      </div>

      {error   && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">{success}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <LogoUploadCard logoPreview={logoPreview} onLogoChange={handleLogoChange} />
          <ThemeColorCard color={settings.primary_color} onChange={c => updateField('primary_color', c)} />
          <ContactInfoCard settings={settings} onChange={updateField} />
        </div>

        <div className="lg:col-span-2 space-y-6">
          <ShopNameCard settings={settings} onChange={updateField} />
          <ShopTagCard settings={settings} onChange={updateField} />
          <DeliverySettingsCard settings={settings} onToggle={toggleField} />
          <PaymentSettingsCard settings={settings} onToggle={toggleField} />
          <KhrRateCard rate={settings.khr_rate} onChange={v => updateField('khr_rate', v)} />
        </div>
      </div>

      <BannerManageSection
        banners={banners}
        onAddClick={() => {
          setShowBannerForm(true);
          setBannerForm({ image_url: '', title: '', link_url: '', sort_order: banners.length });
          setBannerFile(null);
          setBannerPreview('');
        }}
        onToggleStatus={toggleBannerStatus}
        onDelete={handleDeleteBanner}
      />

      {showBannerForm && (
        <BannerFormDialog
          form={bannerForm}
          onFormChange={(field, value) => setBannerForm(prev => ({ ...prev, [field]: value }))}
          preview={bannerPreview}
          onFileChange={handleBannerFileChange}
          uploading={uploadingBanner}
          saving={savingBanner}
          onConfirm={handleAddBanner}
          onClose={() => setShowBannerForm(false)}
        />
      )}
    </div>
  );
};

export default ShopSettingsPage;